import { CommonModule } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  OnChanges,
  OnInit,
  ViewChild,
} from '@angular/core';
import { FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { environment } from '../../../../environments/environment';
import { CommonService } from '../../../api/services/common.service';
import { FORM_ID } from '../../../helpers/form-id.enum';

@Component({
  selector: 'app-input-search-integration',
  imports: [CommonModule, FormsModule, ReactiveFormsModule],
  templateUrl: './input-search-integration.component.html',
  styleUrl: './input-search-integration.component.css',
})
export class InputSearchIntegrationComponent implements OnInit, OnChanges {
  @Input() formGroup!: FormGroup;
  @Input() fields: any;
  @ViewChild('searchInput') searchInput!: ElementRef<HTMLInputElement>;
  @Input() autoFocused: any;
  endpoint = environment.api_endpoint;
  fieldsData: any[] = [];
  showError: string = '';
  showErrors: boolean = false;
  userLoading: { [userId: string]: boolean } = {};
  allFieldsData: any[] = [];
  isLoading: boolean = false;
  formId: any;
  selectedIntegrations: any[] = [];
  isDependencySelected: boolean = false;

  constructor(
    private cdr: ChangeDetectorRef,
    private commonService: CommonService,
  ) {}

  ngOnInit() {
    this.subscribeToControlSelection();
    const control = this.formGroup?.get(this.fields?.fieldName);
    if (control) {
      if (control.value) {
        this.selectedIntegrations = Array.isArray(control.value)
          ? [...control.value]
          : [control.value];

        // ✅ Show selected integrations in edit mode
        if (!this.fieldsData.length) {
          this.fieldsData = [...this.selectedIntegrations];
          this.allFieldsData = [...this.selectedIntegrations];
        }
      }

      control.valueChanges.subscribe((value) => {
        this.validateSelection();

        this.selectedIntegrations = Array.isArray(value)
          ? [...value]
          : value
            ? [value]
            : [];

        this.cdr.detectChanges();
      });
    }
  }

  ngOnChanges() {
    if (!this.autoFocused) return;

    setTimeout(() => {
      this.searchInput?.nativeElement.focus();
    });
  }

  private subscribeToControlSelection(): void {
    const dependencyTrigger =
      this.fields?.fieldDependencies?.[0]?.trigger ||
      this.fields?.fieldDependencies?.[0]?.condition?.[0]?.conditions?.[0]
        ?.value;

    if (!dependencyTrigger) {
      return;
    }

    const dependencyField = this.formGroup?.get(dependencyTrigger);

    if (!dependencyField) {
      return;
    }

    this.processDependencyValue(dependencyField.value);

    dependencyField.valueChanges.subscribe((selectedValue: any) => {
      this.processDependencyValue(selectedValue);
    });
  }

  private processDependencyValue(selectedValue: any): void {
    this.isDependencySelected = !!selectedValue;

    if (!selectedValue) {
      this.allFieldsData = [];
      this.fieldsData = [];
      this.isLoading = false;
      this.validateSelection();
      this.cdr.detectChanges();
      return;
    }

    // Case 1: selectedValue already has integrations array
    if (
      selectedValue?.integrations &&
      Array.isArray(selectedValue.integrations) &&
      selectedValue.integrations.length > 0
    ) {
      this.setIntegrationsData(selectedValue.integrations);
      return;
    }

    // Case 2: selectedValue is an object or string without integrations array
    const controlIdentifier =
      typeof selectedValue === 'object'
        ? selectedValue._id || selectedValue.name || selectedValue.text
        : selectedValue;

    if (controlIdentifier) {
      this.isLoading = true;
      this.cdr.detectChanges();

      const formId =
        this.fields?.options?.source?.formID || FORM_ID.CONTROLS_LIST;

      this.commonService.loadListWithOptions(formId).subscribe({
        next: (res: any) => {
          const records = res?.data?.records || [];
          const matchedControl = records.find(
            (c: any) =>
              c._id === controlIdentifier ||
              c.name === controlIdentifier ||
              c.text === controlIdentifier ||
              c.value === controlIdentifier,
          );

          if (matchedControl?.integrations?.length) {
            this.setIntegrationsData(matchedControl.integrations);
          } else if (
            selectedValue?.integrations &&
            Array.isArray(selectedValue.integrations)
          ) {
            this.setIntegrationsData(selectedValue.integrations);
          } else {
            this.setIntegrationsData([]);
          }
          this.isLoading = false;
          this.cdr.detectChanges();
        },
        error: () => {
          if (
            selectedValue?.integrations &&
            Array.isArray(selectedValue.integrations)
          ) {
            this.setIntegrationsData(selectedValue.integrations);
          } else {
            this.setIntegrationsData([]);
          }
          this.isLoading = false;
          this.cdr.detectChanges();
        },
      });
    } else {
      this.setIntegrationsData([]);
    }
  }

  private setIntegrationsData(integrations: any[]): void {
    this.allFieldsData = integrations || [];
    this.fieldsData = [...this.allFieldsData];

    const existingValue = this.formGroup?.get(this.fields?.fieldName)?.value;
    this.selectedIntegrations = Array.isArray(existingValue)
      ? [...existingValue]
      : existingValue
        ? [existingValue]
        : [];

    this.sortSelectedFirst();
    this.validateSelection();
    this.cdr.detectChanges();
  }

  private sortSelectedFirst(): void {
    this.fieldsData = [...this.fieldsData].sort((a, b) => {
      const aSelected = this.isSelected(a);
      const bSelected = this.isSelected(b);

      if (aSelected && !bSelected) return -1;
      if (!aSelected && bSelected) return 1;

      return 0;
    });
  }

  applyFilters(searchText: string) {
    const search = searchText?.toLowerCase() || '';

    this.fieldsData = (this.allFieldsData || []).filter(
      (option: any) => !search || option.name?.toLowerCase().includes(search),
    );
  }

  onKeyUpSearch(searchText: string): void {
    if (searchText.length >= 3 || searchText.length === 0) {
      this.applyFilters(searchText);
    }
  }

  resetSearch(): void {
    if (this.searchInput) {
      this.searchInput.nativeElement.value = '';
    }

    this.fieldsData = [...this.allFieldsData];

    this.selectedIntegrations = [];

    const control = this.formGroup.get(this.fields.fieldName);

    if (control) {
      control.setValue([]);
      control.markAsPristine();
      control.markAsUntouched();
      control.updateValueAndValidity();
    }

    this.showError = '';
  }

  validateSelection(): boolean {
    const control = this.formGroup.get(this.fields.fieldName);
    if (!control) {
      return true;
    }
    const hasValue =
      control.value &&
      (Array.isArray(control.value)
        ? control.value.length > 0
        : !!control.value);

    if (this.isDependencySelected && !hasValue) {
      this.showError =
        control.touched || control.dirty ? 'Please select integration' : '';
      control.setErrors({ required: true });
      return false;
    } else {
      this.showError = '';
      control.setErrors(null);
      return true;
    }
  }

  selectIntegration(item: any, event: any): void {
    const control = this.formGroup.get(this.fields.fieldName);
    if (event.target.checked) {
      this.selectedIntegrations = [...this.selectedIntegrations, item];
    } else {
      this.selectedIntegrations = this.selectedIntegrations.filter(
        (integration) => integration.name !== item.name,
      );
    }

    if (control) {
      control.setValue(this.selectedIntegrations);
      control.markAsDirty();
      control.markAsTouched();
      control.updateValueAndValidity();
    }

    this.sortSelectedFirst();

    this.cdr.detectChanges();
  }

  isSelected(item: any): boolean {
    return this.selectedIntegrations.some(
      (integration) => integration.name === item.name,
    );
  }

  trackById(index: number, item: any) {
    return item._id;
  }
}
